import React, { useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

import EventCard from "../components/Cards/EventCard";
import SVGSeparator from "../services/SVGSeparator";
import background from "../assets/img/ensemble_table_2.webp";
import { EventsContext } from "../services/EventsContext";

/* Apparition au scroll (meme hook que le reste du site). */
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
};

const RevealCard = ({ event, index }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${(index % 2) * 80}ms` }}
    >
      <EventCard {...event} />
    </div>
  );
};

const EventsPage = () => {
  const { t } = useTranslation();
  const { events } = useContext(EventsContext);

  return (
    <div style={{ backgroundColor: "#FBF7EF" }}>
      <Helmet>
        <title>Événements | Au Coing du Ventoux</title>
        <meta name="description" content="Participez à nos événements autour de l'alcool de coing et découvrez nos dégustations et salons." />
        <meta name="keywords" content="dégustation alcool de coing, salon spiritueux, événements artisanaux, Mont Ventoux" />
        <meta property="og:title" content="Événements | Au Coing du Ventoux" />
        <meta property="og:description" content="Participez à nos événements autour de l'alcool de coing et découvrez nos dégustations et salons." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aucoingduventoux.com/events" />
        <meta property="og:image" content="https://aucoingduventoux.com/images/events.jpg" />
        <link rel="canonical" href="https://aucoingduventoux.com/events" />
      </Helmet>

      {/* Section Héro */}
      <div className="relative h-screen min-h-[560px] overflow-hidden">
        <img src={background} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(30,20,8,0.6) 0%, rgba(30,20,8,0.45) 45%, rgba(30,20,8,0.7) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 60%)" }} />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <h1 className="font-sans text-4xl md:text-5xl lg:text-6xl leading-tight max-w-4xl" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
            {t("events.bigtitle")}
          </h1>
          <SVGSeparator width={120} height={50} className="" />
          <p className="mt-4 font-nunito text-lg md:text-xl max-w-2xl text-stone-100/90" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.6)" }}>
            {t("events.bigdescription")}
          </p>
        </div>
      </div>

      {/* Intro */}
      <div className="text-center max-w-3xl mx-auto px-6 pt-16">
        <span className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-800">
          Agenda
        </span>
        <h2 className="font-sans mt-4 text-3xl md:text-4xl text-stone-900">
          {t("events.title")}
        </h2>
        <p className="mt-4 font-nunito text-stone-600">{t("events.description")}</p>
        <SVGSeparator width={120} height={50} className="mx-auto" />
      </div>

      {/* Liste des événements */}
      <div className="max-w-3xl mx-auto px-6 pb-24 pt-6">
        {events.length > 0 ? (
          <div className="grid grid-cols-1 gap-8">
            {events.map((event, index) => (
              <RevealCard key={event.id} event={event} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <h2 className="font-sans text-2xl text-stone-800">{t("events.no_upcoming")}</h2>
            <p className="font-nunito text-stone-500 mt-3">{t("events.come_back_later")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;